import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Tag } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar, faList, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import dayjs from 'dayjs'

import ButtonCustom from '../../../../../../../../Components/ButtonCustom'
import { TitleH1 } from '../../../../../../../../Components/CustomUi/titleH1'
import { Select } from '../../../../../../../../Components/CustomUi/Select'
import { Render } from '../../../../../../../../Components/CustomUi/render'
import Carregando from '../../../../../../../../Components/CustomUi/loading'
import DatePickerCustom from '../../../../../../../../Components/CustomUi/datePicker'
import { Inline } from '../../../../../../../../Components/CustomUi/inline'
import { ModalPopOver } from '../../../../../../../../Components/CustomUi/modalPopOver'
import {
    camposDoStatus,
    iconeStatus,
    opcoesDeStatus,
    paletaStatus,
    statusDesabilitados,
    statusLabel,
    type PaletaStatus,
} from '../../meta/statusTimeline'
import { ALTURA_LISTA_SELECT, FormAlterarStatus, ModalWrapper } from './styled'

/** Largura do modal. Usada tambem pelo compositor, que o cobre por inteiro. */
const LARGURA_MODAL = 420


/**
 * Item do histórico de status.
 *
 * `usuario_nome` e `data_hora` são opcionais: `StatusTypes`
 * (services/Laboratorio) não declara o nome do usuário, logo o campo pode não
 * vir na resposta.
 */
interface ItemStatusOs {
    cod?: number
    status?: number
    status_nome?: string
    usuario_nome?: string
    data_hora?: string
}

interface Props {
    numero_pedido: string | number
    abrirStatus: boolean
    setAbrirStatus: React.Dispatch<React.SetStateAction<boolean>>
    status_selecionada: string
    setStatus_selecionada: React.Dispatch<React.SetStateAction<string>>
    setStatus_selecionada_id: React.Dispatch<React.SetStateAction<number>>
    status_selecionada_id: number
    data_entrega_lab: string
    setData_entrega_lab: React.Dispatch<React.SetStateAction<string>>
    dataStatusOs: ItemStatusOs[]
    alterarStatus: Function
    loading_status: boolean
    fecharModal: Function
}

/** Props do item da timeline. `ghost` é a variante de pré-visualização. */
interface ItemTimelineProps {
    icone?: IconDefinition
    paleta: PaletaStatus
    titulo: string
    autor?: string
    legenda: string
    /** Último item do histórico: recebe destaque de "status atual". */
    atual?: boolean
    /** Pré-visualização do status escolhido (borda tracejada). */
    ghost?: boolean
    /** Oculta a linha vertical — usada no item que encerra a lista. */
    encerra?: boolean
}

/**
 * Item único da timeline, com variantes por `data-*`. A pré-visualização usa o
 * MESMO componente (variante `ghost`), e não um componente paralelo, para os
 * dois nunca divergirem visualmente.
 */
const ItemTimeline: React.FC<ItemTimelineProps> = ({
    icone,
    paleta,
    titulo,
    autor,
    legenda,
    atual,
    ghost,
    encerra,
}) => (
    <div
        className='StatusTimeLine__item'
        data-atual={!!atual}
        data-ghost={!!ghost}
        style={ghost ? { borderColor: paleta.borda } : undefined}
    >
        <div className='StatusTimeLine__item__marcador'>
            <span
                className='StatusTimeLine__item__marcador__icone'
                style={{
                    backgroundColor: paleta.fundo,
                    border: `1px ${ghost ? 'dashed' : 'solid'} ${paleta.borda}`,
                    color: paleta.cor,
                }}
            >
                {!!icone && <FontAwesomeIcon fontSize={12} icon={icone} />}
            </span>
            <Render condition={!encerra}>
                <div className='StatusTimeLine__item__marcador__linha' />
            </Render>
        </div>

        <div className='StatusTimeLine__item__conteudo'>
            <h3 style={{ color: paleta.cor }}>{titulo}</h3>
            <Render condition={!!autor}>
                <span>{autor}</span>
            </Render>
            <p>{legenda}</p>
        </div>
    </div>
)

/**
 * Formata a data do histórico. A hora só entra quando o backend realmente a
 * envia: `data_hora` pode chegar apenas com a data e, nesse caso, exibir
 * "00:00" em todos os itens seria informação falsa.
 */
function formatarDataHora(valor?: string): string {
    if (!valor) return ''

    const data = dayjs(valor)
    if (!data.isValid()) return ''

    const horario = /(\d{2}):(\d{2})/.exec(valor)
    const tem_hora = !!horario && !(horario[1] === '00' && horario[2] === '00')

    return data.format(tem_hora ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
}

export const StatusTimeLine: React.FC<Props> = ({
    numero_pedido,
    abrirStatus,
    setAbrirStatus,
    status_selecionada,
    setStatus_selecionada,
    setStatus_selecionada_id,
    status_selecionada_id,
    data_entrega_lab,
    setData_entrega_lab,
    dataStatusOs,
    alterarStatus,
    loading_status,
    fecharModal,
}) => {
    const [openSelect, setOpenSelect] = useState<boolean>(false)
    /** Compositor colapsado por padrão: ler o histórico é a ação principal. */
    const [compositorAberto, setCompositorAberto] = useState<boolean>(false)

    const refHistorico = useRef<HTMLDivElement>(null)
    /** Rodape do modal: o popover e ancorado sobre ele, cobrindo o gatilho. */
    const refRodape = useRef<HTMLElement>(null)
    /** Coordenadas do popover, medidas do gatilho (viewport). */
    const [posicao, setPosicao] = useState<{ top: number; left: number } | null>(null)

    /** Último item = status atual da OS (a lista chega em ordem ascendente). */
    const status_atual = useMemo(
        () => (dataStatusOs ?? [])[(dataStatusOs ?? []).length - 1],
        [dataStatusOs]
    )

    const opcoes = useMemo(() => opcoesDeStatus(), [])
    const desabilitados = useMemo(
        () => statusDesabilitados(status_atual?.status ?? null),
        [status_atual]
    )

    // Campos exigidos pelo status ESCOLHIDO no select (e não pelo atual da OS):
    // a data é informada na mesma ação que promove a OS.
    const campos = useMemo(() => camposDoStatus(status_selecionada_id), [status_selecionada_id])
    const campo_data = campos.find((item) => item.campo === 'data_entrega_lab')

    const paleta_atual = status_atual?.status ? paletaStatus(status_atual.status) : null
    const paleta_selecionada = status_selecionada_id ? paletaStatus(status_selecionada_id) : null

    /** Ao abrir, o scroll do histórico ancora no FIM (evento mais recente). */
    useEffect(() => {
        if (!abrirStatus || loading_status) return

        const elemento = refHistorico.current
        if (elemento) elemento.scrollTop = elemento.scrollHeight
    }, [abrirStatus, loading_status, dataStatusOs])

    /**
     * Fechar o modal descarta o estado local do compositor.
     *
     * A posicao tambem e descartada: o rodape muda de lugar conforme a altura do
     * historico, e reaproveitar a medicao anterior abriria o popover deslocado.
     */
    useEffect(() => {
        if (!abrirStatus) {
            setCompositorAberto(false)
            setPosicao(null)
        }
    }, [abrirStatus])

    /**
     * Pré-preenche a data com hoje quando o status escolhido a pede e o campo
     * está vazio. Continua editável (e apagável) pelo usuário.
     */
    useEffect(() => {
        if (campo_data && !data_entrega_lab) {
            setData_entrega_lab(dayjs().toISOString())
        }
    }, [campo_data, data_entrega_lab, setData_entrega_lab])

    /**
     * Abre o compositor ancorado SOBRE o rodape, cobrindo o gatilho.
     *
     * `ModalPopOver` posiciona por coordenadas fixas de viewport, e este modal
     * tem altura variavel (depende do tamanho do historico) — por isso a posicao
     * e medida no momento da abertura, e nao fixada por constante.
     *
     * Mede o RODAPE, e nao o botao: o popover tem a largura do modal, logo precisa
     * alinhar pela borda do modal — pelo botao ficaria deslocado pelo padding.
     *
     * A altura do popover nao e conhecida antes de montar (`height='auto'`), logo
     * a ancora e a borda SUPERIOR do rodape: o popover cresce para baixo a partir
     * dali, cobrindo o gatilho.
     */
    function abrirCompositor() {
        const rodape = refRodape.current?.getBoundingClientRect()

        if (rodape) {
            setPosicao({ top: rodape.top, left: rodape.left })
        }

        setCompositorAberto(true)
    }

    /** Cancelar volta ao colapsado, limpando seleção e pré-visualização. */
    function cancelarAlteracao() {
        setStatus_selecionada('')
        setStatus_selecionada_id(null)
        setData_entrega_lab('')
        setOpenSelect(false)
        setCompositorAberto(false)
    }

    const total_itens = (dataStatusOs ?? []).length
    const historico_vazio = loading_status || total_itens === 0

    return (
        <Modal
            open={abrirStatus}
            footer={null}
            closable={false}
            // `Esc` e clique no overlay passam pelo MESMO fluxo do botão de fechar:
            // antes chamavam só `setAbrirStatus(false)`, deixando `status_selecionada`
            // e a data sujas, que reapareciam na próxima abertura do modal.
            onCancel={() => fecharModal()}
            width={LARGURA_MODAL}
            style={{ left: '300px' }}
            bodyStyle={{ padding: 0 }}
            // O compositor e um ModalPopOver: renderiza por portal em `#portal`,
            // FORA desta arvore, com `z-index: 1001` fixo no proprio componente.
            // O padrao do antd (`zIndexPopupBase + 10` = 1010) deixaria o popover
            // atras do modal, invisivel — por isso o modal cede a camada.
            zIndex={1000}
            destroyOnClose
        >
            <ModalWrapper>
                {/* ----------------------------- HEADER ---------------------------- */}
                <header className='StatusTimeLine__header'>
                    <div className='StatusTimeLine__header__linha'>
                        <TitleH1>{`Pedido ${numero_pedido}`}</TitleH1>
                        <ButtonCustom
                            type='tertiary'
                            danger
                            isCircle
                            title='Fechar'
                            onClick={fecharModal}
                            icon={<FontAwesomeIcon icon={faXmark} />}
                        />
                    </div>

                    {/* Chip do status atual: mesmo ícone e mesma cor da timeline. */}
                    <Render condition={!!paleta_atual}>
                        <Tag
                            color={paleta_atual?.fundo}
                            style={{ color: paleta_atual?.cor, borderColor: paleta_atual?.borda }}
                        >
                            <FontAwesomeIcon fontSize={11} icon={iconeStatus(status_atual?.status)} />
                            {`${statusLabel(status_atual?.status, status_atual?.status_nome)}`}
                        </Tag>
                    </Render>
                </header>

                {/* ---------------------------- HISTÓRICO -------------------------- */}
                <div
                    className='StatusTimeLine__historico'
                    data-vazio={historico_vazio}
                    ref={refHistorico}
                >
                    <Render condition={loading_status}>
                        <Carregando
                            animation
                            justifyContent='center'
                            size='default'
                            legenda='Carregando...'
                        />
                    </Render>

                    <Render condition={!loading_status}>
                        {(dataStatusOs ?? []).map((item, index) => {
                            const ultimo = index === total_itens - 1
                            // Sem pré-visualização, a linha do último item não é
                            // desenhada; com ela, a linha liga o real ao ghost.
                            const encerra = ultimo && !status_selecionada_id

                            return (
                                <ItemTimeline
                                    key={item?.cod ?? index}
                                    icone={iconeStatus(item?.status)}
                                    paleta={paletaStatus(item?.status)}
                                    titulo={statusLabel(item?.status, item?.status_nome)}
                                    autor={item?.usuario_nome}
                                    legenda={formatarDataHora(item?.data_hora)}
                                    atual={ultimo}
                                    encerra={encerra}
                                />
                            )
                        })}
                    </Render>
                </div>

                {/* --------------------------- GATILHO ----------------------------- */}
                {/* Só o gatilho fica ancorado no rodapé: o formulário em si vive no
                    ModalPopOver, fora desta árvore. */}
                <footer className='StatusTimeLine__compositor' ref={refRodape}>
                    <ButtonCustom
                        type='secondary'
                        disabled={compositorAberto}
                        onClick={abrirCompositor}
                        icon={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Alterar status
                    </ButtonCustom>
                </footer>
            </ModalWrapper>

            {/* ----------------------------- COMPOSITOR ---------------------------- */}
            {/* Em popover (portal para #portal), e não dentro do modal: assim o
                histórico não cede altura nem re-renderiza ao abrir o formulário.
                `onClose` reusa `cancelarAlteracao` para que fechar pelo `×` limpe a
                seleção e a pré-visualização, igual ao botão Cancelar.

                Só monta com a posição já medida: sem isso o popover apareceria uma
                vez nas coordenadas default antes de saltar para cima do gatilho. */}
            <Render condition={!!posicao}>
                <ModalPopOver
                    title='Alterar status'
                    open={compositorAberto}
                    onClose={cancelarAlteracao}
                    width={350}
                    height='auto'
                    top={posicao?.top - 100}
                    left={posicao?.left - 20}
                >
                    <FormAlterarStatus data-select-aberto={openSelect}>
                        {/* status `ANÁLISADA` não pode ser alterada manualmente, isso foi pedido 
                        pelo RONALDO no dia 28/08/26:08h14 - passado pelo Cley no meu privado do whatsapp.
                        o status `ANÁLISADA` será feito automaticamente pelo backend */}
                        <Select
                            label='Novo status'
                            value={status_selecionada}
                            icon={status_selecionada_id ? iconeStatus(status_selecionada_id) : faList}
                            placeholder='Selecione o novo status'
                            onChange={setStatus_selecionada}
                            onChangeID={setStatus_selecionada_id}
                            open={openSelect}
                            trigger={setOpenSelect}
                            items={opcoes}
                            descricaoItem='nome'
                            idItem='cod'
                            disabledItens={desabilitados}
                            disabled={loading_status}
                            maxHeight={ALTURA_LISTA_SELECT}
                        />

                        {/* Entrega/envio das amostras ao laboratório: só para os status
                            que o exigem. Opcional — vazia, o campo não é enviado e o
                            valor já gravado no backend é preservado. */}
                        <Render condition={!!campo_data}>
                            <DatePickerCustom
                                label={campo_data?.label}
                                icon={faCalendar}
                                opcional
                                desabilitado={loading_status}
                                value={data_entrega_lab ? dayjs(data_entrega_lab) : null}
                                onChange={(valor) =>
                                    setData_entrega_lab(valor ? dayjs(valor).toISOString() : '')
                                }
                                width='100%'
                            />
                        </Render>

                        <Inline justifyContent='flex-end'>
                            <ButtonCustom
                                type='tertiary'
                                disabled={loading_status}
                                onClick={cancelarAlteracao}
                                danger
                            >
                                Cancelar
                            </ButtonCustom>
                            <ButtonCustom
                                onClick={alterarStatus}
                                loading={loading_status}
                                disabled={!status_selecionada_id}
                            >
                                Salvar status
                            </ButtonCustom>
                        </Inline>
                    </FormAlterarStatus>
                </ModalPopOver>
            </Render>
        </Modal>
    )
}
